// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import React, { useState } from 'react';
import InterviewCard from '../../../components/Interview/InterviewCard';
import ProgressIndicator from '../../../components/Interview/ProgressIndicator';
import Input from '../../../components/Interview/shared/Input';
import Button from '../../../components/Interview/shared/Button';
import PrivacyPolicyModal from '../../../components/Interview/PrivacyPolicyModal';
import { interviewAPI } from '../../../services/interview/api';
import { SendOTPResponse } from '../../../types/interview';
import './IdentityVerification.css';

interface IdentityVerificationProps {
  name: string;
  email: string;
  jobTitle: string;
  jobMeta: string;
  sessionToken: string;
  assessmentType?: 'interview' | 'skill_assessment';
  onNext: () => void;
}

const IdentityVerification: React.FC<IdentityVerificationProps> = ({
  name,
  email,
  jobTitle,
  jobMeta,
  sessionToken,
  assessmentType,
  onNext
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);

  // Generate assessment title based on type
  const assessmentTitle = assessmentType === 'skill_assessment' ? 'AI Skill Assessment' : 'Interview';

  const handleSendCode = async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const response: SendOTPResponse = await interviewAPI.sendOTP({
        token: sessionToken,
        workEmail: email
      });

      if (response.success) {
        onNext();
      } else {
        setError(response.message || 'Failed to send verification code');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
      console.error('Send OTP error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrivacyClick = (e: React.MouseEvent<HTMLAnchorElement>): void => {
    e.preventDefault();
    setIsPrivacyModalOpen(true);
  };

  return (
    <div className="identity-container">
      <InterviewCard>
        <ProgressIndicator totalSteps={3} currentStep={1} />

        <div className="identity-jobBadge">
          <div className="identity-jobIcon">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
            </svg>
          </div>
          <div className="identity-jobInfo">
            <p className="identity-jobTitle">{jobTitle}</p>
            <p className="identity-jobMeta">{jobMeta}</p>
          </div>
        </div>

        <h2 className="identity-heading">You're invited to your {assessmentTitle}</h2>
        <p className="identity-subheading">
          Before we begin, please confirm your details so we can verify it is you. No face capture is used.
        </p>

        {error && <div className="identity-errorMessage">{error}</div>}

        <div className="identity-formSection">
          <Input label="Full name" value={name} disabled />
          <Input label="Email" value={email} disabled />
        </div>

        <Button onClick={handleSendCode} disabled={isLoading}>
          {isLoading ? 'Sending...' : 'Send verification code'}
        </Button>

        <p className="identity-footer">
          By continuing you agree to our{' '}
          <a href="#" className="identity-privacyLink" onClick={handlePrivacyClick}>
            Privacy Policy
          </a>
          . Your recording will be stored for evaluation only.
        </p>
      </InterviewCard>

      <PrivacyPolicyModal isOpen={isPrivacyModalOpen} onClose={() => setIsPrivacyModalOpen(false)} />
    </div>
  );
};

export default IdentityVerification;
